import type { PriceData } from '../common/models/priceData/priceData';
import type { EpiRecipeStartPageType } from '../episerver/components/pages/EpiRecipeStartPage/epiRecipeStartPageType';

type CategoryName = string;
type CategoryUrlSegment = string;
export type UrlSegmentToCategoryName = Record<CategoryUrlSegment, CategoryName>;
export type CategoryNameToUrlSegment = Record<CategoryName, CategoryUrlSegment>;
export type CategoryNameToContentReference = Record<CategoryName, string>;
export type CategoryNamesUnderLevel1 = Record<CategoryName, boolean>;
export type GetUrlForCategoriesSignature = (args: { categories: string[] }) => string;
export type RecipeQueryParam = 'page' | 'sort' | 'filter' | 'query';
export type SelectedEpiRecipeStartPageType = Pick<
    EpiRecipeStartPageType,
    | 'startpageModules'
    | 'topModules'
    | 'contentLink'
    | 'contentType'
    | 'name'
    | 'startPublish'
    | 'metaDescription'
    | 'preamble'
    | 'popularCategories'
    | 'url'
    | 'pageTypeNameForAnalytics'
>;
export type RecipeCalculationType = 'waste' | 'price';
export type RecipePrioritizationType = 'organic' | 'sugar-free' | 'lactose-free' | 'gluten-free';

export const sectionTypes = {
    recipe: 'recipe',
    shared: 'shared',
    common: 'common',
};

// export const sectionTypes = ['recipe', 'shared', 'common'] as const;
export type RecipeSectionType = keyof typeof sectionTypes;

export const productGroupTypes = ['singleProducts', 'sharedProducts', 'commonProducts'] as const;
export type RecipeProductGroupType = (typeof productGroupTypes)[number];

export enum ItemState {
    Default = 'default',
    Removed = 'removed',
}
export interface RecipeProductException {
    productId: string;
    qty: number | null;
    substituteId: string;
    variantId?: string;
}

export interface RecipeItemState {
    id: string;
    name: string;
    image?: string;
    manufacturer?: string;
    substituteIdentifier: string;
    substituteProductsAmount: number;
    quantity: number | null;
    purchasable: boolean;
    priceData: PriceData;
    totalPriceData: PriceData;
    packageSizeInformation: string;
    belongsToRecipes: SharedRecipe[];
    selectedVarianceCode?: string;

    // derived:
    group: RecipeProductGroupType;
    state: ItemState;
}

export enum RecipeModalTab {
    Overview = 1,
    Products = 2,
}

export const gastrofyAllergies = ['gluten', 'sugar', 'high-lactose', 'low-lactose', 'fodmap'];

export type GastrofyAllergiesType = (typeof gastrofyAllergies)[number];

export const gastrofyDiets = [
    'paleo',
    'vegan',
    'vegetarian',
    'pregnant',
    'low-carb',
    'inflammatory',
    'anti-inflammatory',
];

export type GastrofyDietsType = (typeof gastrofyDiets)[number];

export const gastrofyExcludes = [
    'beef',
    'pork',
    'poultry',
    'fish',
    'shellfish',
    'wild-meat',
    'lamb',
] as const;

export type GastrofyExcludeType = (typeof gastrofyExcludes)[number];

export interface RecipeDiscoveryRequest {
    offset?: number;
    limit: number;
    storeId: string;
    dataFields?: string[];
    extraData?: string[];
    tags?: string[];
    exclude?: GastrofyExcludeType[];
    allergies?: GastrofyAllergiesType[];
    diets?: GastrofyDietsType[];
    randomize?: boolean;
    excludeRecipeIds?: string[];
    externalRecipeIds?: string[];
    mustHaveImage?: boolean;
    isPublished?: boolean;
    matchAllProducts?: boolean;
    productIds?: string[];
    orderBy?: string[];
}

export interface RecipeResolveRequest {
    storeId: string;
    options: Options;
    recipes: RecipeResolveData[];
}

interface ProductException {
    externalId: string;
    substituteIdentifier: string;
    amount: number;
}

interface Options {
    productExceptions?: ProductException[];
    calculation: RecipeCalculationType;
    prioritization?: RecipePrioritizationType;
}

interface RecipeResolveData {
    externalId: string;
    portions: number;
}

export interface SaveRecipeCommentRequest {
    recipeExternalId: string;
    authorName: string;
    comment: string;
}

export interface RecipeComment {
    id: number;
    recipeId: number;
    name: string;
    comment: string;
    date: Date;
    isAdmin: boolean;
}

export interface ImageSticker {
    imageUrl: string;
    keyword: string;
}

export interface RecipePart {
    name?: string | null;
    ingredients: Ingredient[];
}

export interface Ingredient {
    friendlyText: string;
    formattedQuantity: string;
    unit: string;
    sequenceNumber: number;
}

export interface CookingInstructions {
    title: string;
    instructions: string[];
}

export interface RecipeCategory {
    order: number;
    categories: string[];
}

export interface SharedRecipe {
    code: string;
    name: string;
}
export interface RecipeProps {
    id: number;
    externalId: string;
    name: string;
    imageUrl: string;
    stickerUrl?: string;
    portions: number;
    portionsUnit: string;
    parts: RecipePart[];
    averageRating: number;
    ratingPercent: number;
    ratingCount: number;
    preamble: string;
    climateImpactKg: number | null;
    ovenTemperature: string;
    cookingTime: string;
    prepTime: string;
    totalTime: string;
    cookingInstructions: CookingInstructions[];
    servingTips: string;
    tips: string;
    numberOfIngredients: number;
    recipeCreator?: string;
    categories: RecipeCategory[];
    comments: RecipeComment[];
    isFoodBox: boolean;
}

export interface RecipeProductSubstitute {
    productId: string;
    qty: number | null;
    substituteId: string;
    variantId?: string;
}
export interface BelongsToRecipeItem {
    recipeId: string;
    rating: number;
    name: string;
}

export interface RecipeProductItem {
    id: string;
    name: string;
    image?: string;
    manufacturer?: string;
    substituteIdentifier: string;
    substituteProductsAmount: number;
    quantity: number;
    priceData: PriceData;
    rowTotalPriceData: PriceData;
    purchasable: boolean;
    packageSizeInformation: string;
    belongsToRecipes: BelongsToRecipeItem[];
    selectedVarianceCode?: string;
}

export interface ResolvedRecipesList {
    goodToHaveProducts: RecipeProductItem[];
    sharedProducts: RecipeProductItem[];
    singleProducts: RecipeProductItem[];
    recipes: ResolvedRecipe[];
    recipesTotalPrice: PriceData;
    recipesProductsTotalAmount: number;
    calculation: RecipeCalculationType;
    prioritization?: RecipePrioritizationType;
}

// domain
export interface ApiRecipePrice {
    consumptionPortion: number;
    consumptionTotal: number;
    portion: PriceData;
    total: PriceData;
}
export interface ResolvedRecipe {
    portions: number;
    rating: number;
    id: string;
    code: string;
    title: string;
    recipePrice: ApiRecipePrice;
    cookingTime?: number;
    imageUrl: string;
}

export interface RecipeSubstitutes {
    recipeProductItems: RecipeProductItem[];
}

export interface ResolveRecipesRequest {
    recipes: { recipeId: string; portions: number }[];
    products?: RecipeProductException[];
    prioritization: RecipePrioritizationType | null;
    calculation: RecipeCalculationType;
}

export interface RecipeCategoryFallbackImages {
    fullWidth?: string;
    mobile?: string;
}
